/**
 * useSyllabusRowProblems — 학습 화면 오른쪽 문제 미리보기의 「강의 행 보기」(설계 FE-20260914 §8, 결정 D10).
 *
 * 강의 행을 누르면 그 행의 **검색 조건 그대로**(강의 화면과 같은 서버 검색 POST /api/v2/exam-papers/problem-search)를
 * **이 학생이 푼 문제**로 좁혀 보여 준다 — 검색 요청의 filters.problem_ids 에 푼 문제 번호를 실으면 서버가 식과 「그리고」로 겹친다.
 * 그래서 난이도·라벨·「정확히」까지 강의 검색과 같다. 화면이 식을 따라 판정하지 않는다(식 판정의 정본은 서버).
 *
 * 푼 문제는 v2 풀이 기록(GET /api/v2/students/{id}/solved-problems?granularity=latest, 100건씩)에서 받는다 —
 * 난이도 배지를 세는 서버 표와 같은 기록이다. 옛 시험 요약(v1 /api/exams/summary)은 쓰지 않는다(오너 2026-09-14).
 * 학생마다 한 번 받아 이 학생 화면이 떠 있는 동안 기억하고(hubSyllabusCache), 행을 바꿀 때마다 검색만 다시 한다(앞 검색은 취소).
 *
 * 남는 차이: 검색은 확정 문제만 돌려주고(검색 규칙), 배지는 미확정 문제의 기록도 센다.
 * 목록은 문제마다 한 번(최근 결과), 배지는 풀 때마다 센다.
 */
import { useEffect, useState } from 'react';
import { fetchAPI } from '../../utils/apiUtils';
import { searchByExpr, describeSyllabusError } from '../SyllabusV2/syllabusApi';
import { solvedCache } from './hubSyllabusCache';

const PER_PAGE = 100;       // 두 API 모두 한 쪽 상한
const SOLVED_PARALLEL = 4;  // 풀이 기록 쪽을 동시에 받는 수
const SEARCH_PARALLEL = 3;  // 검색 쪽을 동시에 받는 수(검색은 요청마다 5초 상한)
const NETWORK_MSG = '서버에 연결할 수 없습니다. 인터넷 연결을 확인하거나 잠시 후 다시 시도해주세요.';

/** 동시에 limit 개씩만 부른다 — 쪽이 많은 학생에서 요청이 한꺼번에 몰리지 않게 */
async function mapLimit(items, limit, fn) {
  const out = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next;
      next += 1;
      out[i] = await fn(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}
const pagesAfterFirst = (total) => Array.from({ length: Math.max(0, (Number.isInteger(total) ? total : 1) - 1) }, (_, i) => i + 2);

/** v2 GET 한 번 — 성공이면 봉투의 data, 실패면 Error 에 { httpStatus, envelope, reason } */
async function getV2(path, getAccessToken, fallbackMsg) {
  const token = await getAccessToken();
  if (!token) {
    const err = new Error('로그인이 필요합니다.');
    err.httpStatus = 401;
    throw err;
  }
  let res;
  try {
    res = await fetchAPI(path, { headers: { Authorization: token, 'Content-Type': 'application/json' } });
  } catch (e) {
    throw new Error(NETWORK_MSG);
  }
  let body = null;
  try { body = await res.json(); } catch (_) { /* 아래에서 상태 코드로 표면화 */ }
  const envelope = body != null && typeof body.status === 'string';
  if (!res.ok || !envelope || body.status !== 'success') {
    const err = new Error((envelope && body.message) || `${fallbackMsg} (${res.status})`);
    err.httpStatus = res.status;
    err.envelope = envelope;
    err.reason = envelope ? (body.data?.reason ?? null) : null;
    throw err;
  }
  return body.data;
}

/** 이 학생이 푼 문제 → Map(문제 번호 → 최근 결과 true·false·null). v2 풀이 기록을 끝 쪽까지 */
export async function fetchSolvedLatest(studentId, getAccessToken) {
  const url = (page) => `/api/v2/students/${studentId}/solved-problems?granularity=latest&page=${page}&per_page=${PER_PAGE}`;
  const msg = '풀이 기록을 불러오지 못했습니다.';
  const first = await getV2(url(1), getAccessToken, msg);
  const rest = await mapLimit(pagesAfterFirst(first?.total_pages), SOLVED_PARALLEL, (p) => getV2(url(p), getAccessToken, msg));
  const map = new Map();
  [first, ...rest].forEach((d) => (Array.isArray(d?.items) ? d.items : []).forEach((it) => {
    // 쪽을 나눠 받는 사이 새 풀이가 끼면 같은 문제가 두 쪽에 걸칠 수 있다 — 먼저 받은(더 최근) 것을 둔다
    if (it && it.problem_id != null && !map.has(it.problem_id)) map.set(it.problem_id, it.latest_result ?? null);
  }));
  return map;
}

/** 강의 행 식 그대로 × 푼 문제 번호 → 맞는 문제 번호(검색 순서, 중복 없이). 끝 쪽까지 */
export async function fetchRowProblemIds({ expr, problemIds, signal = null }, getAccessToken) {
  if (!Array.isArray(problemIds) || !problemIds.length) return [];
  const filters = { problem_ids: problemIds.join(',') };
  const page = (p) => searchByExpr({ expr, filters, page: p, perPage: PER_PAGE, signal }, getAccessToken);
  const first = await page(1);
  const rest = await mapLimit(pagesAfterFirst(first.totalPages), SEARCH_PARALLEL, page);
  const seen = new Set();
  const ids = [];
  [first, ...rest].forEach((d) => d.items.forEach((it) => {
    if (it && it.problem_id != null && !seen.has(it.problem_id)) {
      seen.add(it.problem_id);
      ids.push(it.problem_id);
    }
  }));
  return ids;
}

/** 학생별 푼 문제 — 이 학생 화면이 떠 있는 동안 한 번(실패는 기억하지 않아 다음 행에서 다시 묻는다) */
function loadSolved(studentId, getAccessToken) {
  let p = solvedCache.get(studentId);
  if (!p) {
    p = fetchSolvedLatest(studentId, getAccessToken).catch((e) => {
      if (solvedCache.get(studentId) === p) solvedCache.delete(studentId);
      throw e;
    });
    solvedCache.set(studentId, p);
  }
  return p;
}

const IDLE = { status: 'idle', problems: [], resultById: null, error: null };
const LOADING = { status: 'loading', problems: [], resultById: null, error: null };

/**
 * row: { key, name, query } | null → { status: 'idle'|'loading'|'ok'|'error', problems: [{ problem_id }], resultById: Map, error }.
 * problems 는 강의 검색 순서, resultById 는 푼 문제 전부의 최근 결과(「틀린 문제만」·정오 색에 쓴다).
 */
export function useSyllabusRowProblems(studentId, row, getAccessToken) {
  const [state, setState] = useState({ row: null, ...IDLE });
  useEffect(() => {
    if (!row || !Number.isInteger(studentId)) { setState({ row: null, ...IDLE }); return undefined; }
    const ctrl = new AbortController();
    setState({ row, ...LOADING });
    (async () => {
      let solved;
      try {
        solved = await loadSolved(studentId, getAccessToken);
      } catch (e) {
        if (!ctrl.signal.aborted) setState({ row, status: 'error', problems: [], resultById: null, error: e?.message || '풀이 기록을 불러오지 못했습니다.' });
        return;
      }
      if (ctrl.signal.aborted) return;
      try {
        const ids = await fetchRowProblemIds({ expr: row.query, problemIds: [...solved.keys()], signal: ctrl.signal }, getAccessToken);
        if (!ctrl.signal.aborted) setState({ row, status: 'ok', problems: ids.map((id) => ({ problem_id: id })), resultById: solved, error: null });
      } catch (e) {
        if (ctrl.signal.aborted || e?.name === 'AbortError') return;
        setState({ row, status: 'error', problems: [], resultById: null, error: describeSyllabusError(e) });
      }
    })();
    return () => ctrl.abort();
  }, [row, studentId, getAccessToken]);
  if (!row || !Number.isInteger(studentId)) return IDLE;
  // 행이 바뀐 첫 그림은 효과가 돌기 전이라 state 가 앞 행의 것이다 — 앞 행 목록을 새 행 열쇠로 그려
  // 앞 행 문제 본문을 한 번 더 부르지 않게, 이 행의 결과가 설 때까지 불러오는 중으로 본다
  return state.row === row ? state : LOADING;
}
